import React, { useEffect, useState } from "react";
import "./Reports.css";
import { Circles } from 'react-loader-spinner';
import publicAxios from "../../utils/axios/publicAxios";
import { Link } from 'react-router-dom';

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const reportCategories = ['medium term development plan', 'annual budget', 'annual financial accounts', 'fiscal performance reports', 'Others'];

  // ✅ Fetch and group reports
  // useEffect(() => {
  //   const fetchReports = async () => {
  //     try {
  //       setLoading(true);
  //       const response = await publicAxios.get("/public/reports");
  //       const fetchedReports = response.data.reports || [];

  //       // Group by category
  //       const grouped = fetchedReports.reduce((acc, report) => {
  //         if (!acc[report.category]) acc[report.category] = [];
  //         acc[report.category].push(report);
  //         return acc;
  //       }, {});

  //       // Sort alphabetically by category name
  //       const sortedCategories = Object.keys(grouped).sort();

  //       setReports(
  //         sortedCategories.map((category) => ({
  //           category,
  //           items: grouped[category].sort((a, b) => b.year - a.year),
  //         }))
  //       );

  //       // Open first category by default
  //       if (sortedCategories.length > 0) setActiveCategory(sortedCategories[0]);
  //     } catch (error) {
  //       console.error("Failed to fetch reports:", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchReports();
  // }, []);


  useEffect(() => {
  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await publicAxios.get("/public/reports");
      const fetchedReports = response.data.reports || [];

      // ✅ Group reports by category
      const grouped = fetchedReports.reduce((acc, report) => {
        if (!acc[report.category]) acc[report.category] = [];
        acc[report.category].push(report);
        return acc;
      }, {});

      // ✅ Use the order in reportCategories for sorting
      const orderedCategories = reportCategories.filter(cat => grouped[cat]);

      setReports(
        orderedCategories.map((category) => ({
          category,
          items: grouped[category].sort((a, b) => b.year - a.year), // Newest year first
        }))
      );

      // ✅ Automatically open the first category
      if (orderedCategories.length > 0) setActiveCategory(orderedCategories[0]);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
    } finally {
      setLoading(false);
    }
  };

  fetchReports();
}, []);

  const toggleCategory = (category) => {
    setActiveCategory((prev) => (prev === category ? null : category));
  };

  // ✅ Safe PDF download handler
  const handleDownload = async (url, title) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error("Failed to fetch PDF file");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${title || "document"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      // fallback: open in new tab if direct download fails
      window.open(url, "_blank");
    }
  };

  return (
    <section className="reports">
      <div className="reports-wrapper">
        <h2 className="reports-title">Planning, Budget & Development Reports</h2>

        <p className="reports-intro">
          Igbo-Eze North Local Government is committed to open governance,
          fiscal transparency, and evidence-based planning. This page provides
          access to key planning and financial documents — including the Medium
          Term Development Plan, Annual Budgets, Financial Accounts, and Fiscal
          Performance Reports. Each section is automatically updated when new
          documents are uploaded.
        </p>

        {loading ?
          <div style={{ display: 'flex', flexDirection: 'column', columnGap: '10px', alignItems: 'center', justifyContent: 'center', height: '300px', color: 'gray' }}>
            <Circles height="50" width="50" color="#008751" ariaLabel="loading" /> <h2>Loading Reports...</h2>
          </div> :
          reports.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', columnGap: '10px', alignItems: 'center', justifyContent: 'center', height: '300px', color: 'gray' }}>
              <p>No reports available.</p>
            </div>
          ) : (
            reports.map(({ category, items }) => (
              <div key={category} className="report-category">
                <div
                  className="report-category-header"
                  onClick={() => toggleCategory(category)}
                >
                  <h3>{category}</h3>
                  <span
                    className={`arrow ${activeCategory === category ? "open" : ""
                      }`}
                  >
                    ▼
                  </span>
                </div>

                <div
                  className={`report-category-content ${activeCategory === category ? "show" : ""
                    }`}
                >
                  {
                      activeCategory === 'medium term development plan' ?

                        <div className="reports-category-section">
                          <h3 className="reports-category-title">Medium Term Development Plan (MTDP)</h3>
                          <p className="reports-category-description">
                            Our Medium Term Development Plan (MTDP) is a 5-year roadmap aimed at promoting
                            inclusive growth, infrastructure renewal, and improved livelihoods for the people
                            of Igbo-Eze North Local Government Area. It serves as a strategic guide to
                            development, policy-making, and resource allocation.
                          </p>

                          <h4 className="reports-category-subtitle">Focus Areas:</h4>
                          <ul className="reports-category-list">
                            <li>Economic Growth & Job Creation</li>
                            <li>Human Capital Development</li>
                            <li>Infrastructure & Basic Service Delivery</li>
                            <li>Good Governance & Institutional Reform</li>
                            <li>Security, Justice & Social Inclusion</li>
                            <li>Environmental Sustainability</li>
                          </ul>
                          <h4>📂 Available Development Plans</h4>
                        </div> :

                        activeCategory === 'annual budget' ?

                        <div className="reports-category-section">
                          <h3 className="reports-category-title">Annual Budgets</h3>
                          <p className="reports-category-description">
                            Every year, the Council prepares and publishes a detailed budget to support projects and services that improve the lives of citizens. Our budgets reflect the priorities of the MTDP and are reviewed by the Local Government Legislature before approval.
                          </p>
                          <h4>📂 Approved Annual Budgets</h4>
                        </div> :

                        activeCategory === 'medium term development plan' ?

                        < div className="reports-category-section">
                          <h3 className="reports-category-title">Annual Financial Accounts</h3>
                          <p className="reports-category-description">
                            The Council prepares audited financial statements annually to ensure accountability and transparency in the use of public funds. These reports are reviewed by the Office of the Auditor-General for Local Governments.
                          </p>
                          <h4>📂 Annual Financial Accounts</h4>
                        </div> :

                        activeCategory === 'medium term development plan' ?

                        < div className="reports-category-section">
                          <h3 className="reports-category-title">Fiscal Performance Reports</h3>
                          <p className="reports-category-description">
                            Fiscal Performance Reports show how much of the approved budget was implemented within the year. They include updates on revenues, expenditures, and major development outcomes.
                          </p>
                          <h4>📂 Fiscal Performance Reports</h4>
                        </div> :

                        null
                  }
                  <table className="reports-table">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Document Title</th>
                        <th>Download (PDF)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((report) => (
                        <tr key={report._id}>
                          <td>{report.year}</td>
                          <td><Link to={`/report/${report.slug}`}>{report.title}</Link></td>
                          <td>
                            {report.pdfUrl ? (
                              <button
                                className="reports-download-link"
                                onClick={() =>
                                  handleDownload(report.pdfUrl, report.title)
                                }
                              >
                                📥 Download PDF
                              </button>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}

        <div className="reports-footer">
          <h3>📢 Citizen Engagement</h3>
          <p>
            Citizens are encouraged to follow development progress and
            participate in planning discussions.
          </p>
          <p>
            Questions and feedback can be sent to:{" "}
            <a href="mailto:prs@igboezenorthlga.en.gov.ng">
              prs@igboezenorthlga.en.gov.ng
            </a>
          </p>
        </div>
      </div>
    </section >
  );
};

export default Reports;
